// API Client - Axios instance with authentication

import axios, { type AxiosInstance, type AxiosError } from 'axios';
import type { ApiResponse } from '@phoenixbill/shared';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8787/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
  response => {
    return response;
  },
  (error: AxiosError<ApiResponse>) => {
    // Handle 401 Unauthorized - clear token and redirect to login
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }

    // Extract error message from API response
    const errorMessage =
      error.response?.data?.error?.message || 'An unexpected error occurred';

    // Return formatted error
    return Promise.reject({
      message: errorMessage,
      code: error.response?.data?.error?.code,
      details: error.response?.data?.error?.details,
      status: error.response?.status,
    });
  }
);

export default apiClient;

// Named exports for backward compatibility
export const api = apiClient;
export { apiClient };

// Helper for raw fetch calls that need the correct API base URL
// Use this instead of fetch('/api/v1/...') which breaks in production
export function apiFetch(path: string, options?: RequestInit): Promise<Response> {
  const baseUrl = import.meta.env.VITE_API_URL || '/api/v1';
  // If path starts with /api/v1, strip it since baseUrl already includes it
  const cleanPath = path.startsWith('/api/v1') ? path.slice(7) : path;
  const url = `${baseUrl}${cleanPath}`;

  // Auto-add auth header if not present
  const headers = new Headers(options?.headers);
  if (!headers.has('Authorization')) {
    const token = localStorage.getItem('token');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }
  if (!headers.has('Content-Type') && options?.method !== 'GET') {
    headers.set('Content-Type', 'application/json');
  }

  return fetch(url, { ...options, headers });
}

// Helper function to handle API errors
export function handleApiError(error: any): string {
  if (error.message) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred';
}
